/**
 * P3 Phase 3: Clinic Payment Cancelled/Abandoned Page
 * 
 * Shown when:
 * - User clicks "back" during Stripe checkout
 * - User closes checkout window
 * - Payment is declined
 * - Checkout session expires
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  XCircle, ArrowLeft, HelpCircle, CreditCard, 
  RefreshCw, Mail, Shield
} from 'lucide-react';

const ClinicPaymentCancel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if this was a specific failure reason
  const reason = searchParams.get('reason');
  
  const getReasonMessage = () => {
    switch (reason) {
      case 'declined':
        return t('paymentCancel.cardDeclined', 'Your card was declined. Please try a different payment method.');
      case 'expired':
        return t('paymentCancel.sessionExpired', 'Your checkout session expired. Please try again.');
      case 'insufficient_funds':
        return t('paymentCancel.insufficientFunds', 'Insufficient funds. Please try a different card.');
      default:
        return t('paymentCancel.defaultMessage', 'Your payment was not completed. No charges were made.');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <Card className="bg-white shadow-xl border-0" data-testid="payment-cancel-page">
          <CardContent className="p-8 text-center">
            {/* Icon */}
            <div className="w-20 h-20 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
              <XCircle className="h-12 w-12 text-orange-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('paymentCancel.title', 'Payment Cancelled')}
            </h1>
            
            <p className="text-gray-600 mb-6">
              {getReasonMessage()}
            </p>
            
            {/* Info box */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <HelpCircle className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {t('paymentCancel.whatHappened', 'What happened?')}
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-gray-400 rounded-full" />
                      {t('paymentCancel.reason1', 'You may have closed the payment window')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-gray-400 rounded-full" />
                      {t('paymentCancel.reason2', 'The payment was declined by your bank')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-gray-400 rounded-full" />
                      {t('paymentCancel.reason3', 'The session may have expired')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Security note */}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
              <Shield className="h-4 w-4" />
              <span>{t('paymentCancel.noCharge', 'No charges were made to your account')}</span>
            </div>
            
            {/* Actions */}
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/clinic/packages')}
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
                data-testid="try-again-btn"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {t('paymentCancel.tryAgain', 'Try Again')}
              </Button>
              
              <Button 
                onClick={() => navigate('/clinic/admin')}
                variant="outline"
                className="w-full"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('paymentCancel.backToDashboard', 'Back to Dashboard')}
              </Button>
            </div>
            
            {/* Support */}
            <div className="mt-8 pt-6 border-t">
              <p className="text-sm text-gray-500 mb-2">
                {t('paymentCancel.needHelp', 'Having trouble with payment?')}
              </p>
              <a 
                href="mailto:support@treatx.health" 
                className="text-blue-600 hover:underline flex items-center justify-center gap-1 text-sm"
              >
                <Mail className="h-4 w-4" />
                support@treatx.health
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClinicPaymentCancel;
